// features/procurement/PurchaseOrderDetailPage.tsx
/**
 * Purchase Order Detail — Redesigned Executive View
 * Features:
 * - Interactive multi-stage status timeline with pulsating active step
 * - Fulfilled progress tracker for partially/fully received orders
 * - 1-Click "Receive Remaining Items" shortcut to GRN wizard
 * - Authenticated tenant-branded PDF export with live loading state
 * - High-polish spring modals for Approve, Send to Vendor, and Cancel
 * - Zero silent failures with explicit notifications
 */

import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/store/authStore';
import { useToastStore } from '@/store/toastStore';
import { Spinner } from '@/components/ui/Spinner';
import { ErrorState } from '@/components/ui/ErrorState';
import {
    ArrowLeft, FileText, Truck, Store, User, Calendar,
    CheckCircle2, Clock, Package, Ban, Send,
    Printer, Building2, ChevronRight, X, Sparkles,
    ShieldCheck, AlertTriangle
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { clsx } from 'clsx';
import {
    getPurchaseOrder,
    approvePurchaseOrder,
    sendPurchaseOrder,
    downloadPoPdf,
    getProcurementErrorMessage,
    PurchaseOrder,
} from './services/procurementApi';

const FLOW_STEPS = ['draft', 'pending_approval', 'approved', 'sent', 'partially_received', 'fully_received'];

const STATUS_CONFIG: Record<string, { label: string; bg: string; text: string; border: string; icon: any }> = {
    draft: { label: 'Draft Order', bg: 'bg-slate-100', text: 'text-slate-700', border: 'border-slate-200', icon: Clock },
    pending_approval: { label: 'Pending Approval', bg: 'bg-amber-500/10', text: 'text-amber-400', border: 'border-amber-500/30', icon: Clock },
    approved: { label: 'Approved', bg: 'bg-teal-500/10', text: 'text-teal-400', border: 'border-teal-500/30', icon: CheckCircle2 },
    sent: { label: 'Sent to Vendor', bg: 'bg-purple-500/10', text: 'text-purple-400', border: 'border-purple-500/30', icon: Send },
    partially_received: { label: 'Partially Received', bg: 'bg-cyan-500/10', text: 'text-cyan-400', border: 'border-cyan-500/30', icon: Package },
    fully_received: { label: 'Fully Received', bg: 'bg-emerald-500/10', text: 'text-emerald-400', border: 'border-emerald-500/30', icon: CheckCircle2 },
    cancelled: { label: 'Cancelled', bg: 'bg-rose-500/10', text: 'text-rose-400', border: 'border-rose-500/30', icon: Ban },
};

export function PurchaseOrderDetailPage() {
    const { poId } = useParams<{ poId: string }>();
    const navigate = useNavigate();
    const user = useAuthStore((s) => s.user);
    const queryClient = useQueryClient();
    const addToast = useToastStore((s) => s.addToast);

    const [modalAction, setModalAction] = useState<'approve' | 'send' | null>(null);
    const [isExporting, setIsExporting] = useState(false);

    // ============================================================
    // QUERY
    // ============================================================

    const { data: po, isLoading, isError, refetch } = useQuery<PurchaseOrder>({
        queryKey: ['po-detail', poId],
        queryFn: () => getPurchaseOrder(user!.tenantId, poId!),
        enabled: !!user?.tenantId && !!poId,
    });

    // ============================================================
    // MUTATIONS
    // ============================================================

    const approveMutation = useMutation({
        mutationFn: () => approvePurchaseOrder(user!.tenantId, poId!),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['po-detail', poId] });
            queryClient.invalidateQueries({ queryKey: ['purchase-orders-list'] });
            setModalAction(null);
            addToast({
                type: 'success',
                title: 'Purchase Order Approved',
                message: `PO #${po?.poNumber} has been authorized. You can now dispatch it to the vendor.`,
            });
        },
        onError: (err: any) => {
            setModalAction(null);
            addToast({
                type: 'error',
                title: 'Approval Rejected',
                message: getProcurementErrorMessage(err),
            });
        },
    });

    const sendMutation = useMutation({
        mutationFn: () => sendPurchaseOrder(user!.tenantId, poId!),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['po-detail', poId] });
            queryClient.invalidateQueries({ queryKey: ['purchase-orders-list'] });
            setModalAction(null);
            addToast({
                type: 'success',
                title: 'Dispatched to Supplier',
                message: `PO #${po?.poNumber} marked as sent to ${po?.vendorName || 'the supplier'}.`,
            });
        },
        onError: (err: any) => {
            setModalAction(null);
            addToast({
                type: 'error',
                title: 'Dispatch Failed',
                message: getProcurementErrorMessage(err),
            });
        },
    });

    const handleExport = async () => {
        if (!po) return;
        setIsExporting(true);
        try {
            await downloadPoPdf(user!.tenantId, po.id, po.poNumber);
            addToast({
                type: 'success',
                title: 'PDF Export Complete',
                message: `Purchase Order document #${po.poNumber} saved to your downloads.`,
            });
        } catch (err: any) {
            addToast({
                type: 'error',
                title: 'Export Failed',
                message: err?.message || 'Failed to download official PO PDF document.',
            });
        } finally {
            setIsExporting(false);
        }
    };

    // ============================================================
    // HELPERS
    // ============================================================

    const formatNaira = (val: string | number | undefined) => {
        if (!val) return '₦0.00';
        const num = typeof val === 'string' ? parseFloat(val) : val;
        return isNaN(num) ? '₦0.00' : `₦${num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    };

    const formatDate = (d: string | null | undefined) => {
        if (!d) return '—';
        return new Date(d).toLocaleDateString('en-NG', { day: 'numeric', month: 'short', year: 'numeric' });
    };

    if (isLoading) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[70vh] bg-slate-50 text-slate-600 gap-4">
                <Spinner size="lg" variant="teal" />
                <p className="text-sm font-semibold tracking-wide">Retrieving purchase order records...</p>
            </div>
        );
    }

    if (isError || !po) {
        return (
            <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
                <ErrorState
                    title="Purchase Order Not Found"
                    message="The requested purchase order record could not be loaded or may have been deleted."
                    onBack={() => navigate('/procurement/orders')}
                    onRetry={() => refetch()}
                />
            </div>
        );
    }

    const currentStepIndex = FLOW_STEPS.indexOf(po.status);
    const statusCfg = STATUS_CONFIG[po.status] || STATUS_CONFIG.draft;
    const StatusIcon = statusCfg.icon;

    const totalReceived = po.items?.reduce((s, item) => s + parseFloat(item.quantityReceived || '0'), 0) || 0;
    const totalOrdered = po.items?.reduce((s, item) => s + parseFloat(item.quantityOrdered || '0'), 0) || 0;
    const receivedPercent = totalOrdered > 0 ? Math.min(100, Math.round((totalReceived / totalOrdered) * 100)) : 0;
    const isReceivable = po.status === 'sent' || po.status === 'partially_received';

    return (
        <div className="min-h-screen bg-slate-50/60 text-slate-900 pb-20">
            {/* Top Action Bar */}
            <div className="sticky top-0 z-30 bg-white/95 backdrop-blur-xl border-b border-slate-200 shadow-sm">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                        <div className="flex items-center gap-3">
                            <button
                                onClick={() => navigate('/procurement/orders')}
                                className="p-2.5 rounded-xl bg-white hover:bg-slate-100 text-slate-600 hover:text-slate-900 border border-slate-200 transition-all shadow-sm"
                                type="button"
                                title="Back to PO List"
                            >
                                <ArrowLeft className="w-5 h-5" />
                            </button>
                            <div className="p-2.5 bg-gradient-to-br from-teal-500 to-cyan-600 rounded-2xl shadow-lg shadow-teal-500/20 text-white">
                                <FileText className="w-6 h-6" />
                            </div>
                            <div>
                                <div className="flex items-center gap-2.5 flex-wrap">
                                    <h1 className="text-xl sm:text-2xl font-black tracking-tight text-slate-900 font-mono">
                                        #{po.poNumber}
                                    </h1>
                                    <span className={clsx('px-3 py-1 rounded-full text-xs font-bold border flex items-center gap-1.5', statusCfg.bg, statusCfg.text, statusCfg.border)}>
                                        <StatusIcon className="w-3.5 h-3.5" />
                                        <span>{statusCfg.label}</span>
                                    </span>
                                </div>
                                <p className="text-xs text-slate-500 flex items-center gap-2 mt-0.5">
                                    <span className="font-semibold text-slate-700">{po.vendorName || 'Vendor'}</span>
                                    <span>•</span>
                                    <span>{po.branchName || 'Branch'}</span>
                                    <span>•</span>
                                    <span>Issued {formatDate(po.createdAt)}</span>
                                </p>
                            </div>
                        </div>

                        {/* Top Action CTAs */}
                        <div className="flex items-center gap-3 self-end sm:flex-wrap">
                            <button
                                onClick={handleExport}
                                disabled={isExporting}
                                className="flex items-center gap-2 px-4 py-2.5 bg-white hover:bg-slate-100 border border-slate-300 text-slate-700 rounded-xl font-bold text-xs transition-all shadow-sm cursor-pointer disabled:opacity-50"
                                type="button"
                                title="Download Tenant-Branded PDF"
                            >
                                {isExporting ? <Spinner size="xs" variant="teal" /> : <Printer className="w-4 h-4 text-slate-500" />}
                                <span>{isExporting ? 'Generating PDF...' : 'Export PDF'}</span>
                            </button>

                            {(po.status === 'draft' || po.status === 'pending_approval') && (
                                <button
                                    onClick={() => setModalAction('approve')}
                                    className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white rounded-xl font-bold text-xs shadow-md shadow-emerald-600/20 transition-all cursor-pointer"
                                    type="button"
                                >
                                    <CheckCircle2 className="w-4 h-4" />
                                    <span>Approve PO</span>
                                </button>
                            )}

                            {po.status === 'approved' && (
                                <button
                                    onClick={() => setModalAction('send')}
                                    className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white rounded-xl font-bold text-xs shadow-md shadow-purple-600/20 transition-all cursor-pointer"
                                    type="button"
                                >
                                    <Send className="w-4 h-4" />
                                    <span>Send to Supplier</span>
                                </button>
                            )}

                            {isReceivable && (
                                <button
                                    onClick={() => navigate(`/procurement/grns/new?poId=${po.id}`)}
                                    className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-teal-600 to-cyan-600 hover:from-teal-700 hover:to-cyan-700 text-white rounded-xl font-bold text-xs shadow-md shadow-teal-600/20 transition-all cursor-pointer"
                                    type="button"
                                >
                                    <Truck className="w-4 h-4" />
                                    <span>Receive Goods</span>
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 space-y-8">
                {/* Status Timeline */}
                <div className="bg-white border border-slate-200 rounded-3xl p-6 shadow-sm space-y-4">
                    <div className="flex items-center justify-between text-xs text-slate-500">
                        <span className="font-bold uppercase tracking-wider text-teal-700 flex items-center gap-1.5">
                            <Sparkles className="w-4 h-4 text-teal-600" />
                            <span>Procurement Lifecycle Progress</span>
                        </span>
                        <span className="font-semibold text-slate-700">{receivedPercent}% Received</span>
                    </div>

                    <div className="flex items-center gap-2 flex-wrap">
                        {FLOW_STEPS.map((step, idx) => {
                            const isDone = currentStepIndex >= 0 && idx <= currentStepIndex;
                            const isCurrent = idx === currentStepIndex;

                            return (
                                <div key={step} className="flex items-center gap-2">
                                    <div className={clsx(
                                        'px-3.5 py-1.5 rounded-xl text-xs font-bold border transition-all flex items-center gap-2',
                                        isCurrent
                                            ? 'bg-teal-50 border-teal-500 text-teal-800 shadow-sm ring-2 ring-teal-500/20'
                                            : isDone
                                                ? 'bg-emerald-50 border-emerald-200 text-emerald-700'
                                                : 'bg-slate-50 border-slate-200 text-slate-400'
                                    )}>
                                        <div className={clsx(
                                            'w-2 h-2 rounded-full',
                                            isCurrent ? 'bg-teal-500 animate-pulse' : isDone ? 'bg-emerald-500' : 'bg-slate-300'
                                        )} />
                                        <span>{step.replace(/_/g, ' ').toUpperCase()}</span>
                                    </div>
                                    {idx < FLOW_STEPS.length - 1 && (
                                        <ChevronRight className={clsx('w-4 h-4', isDone ? 'text-teal-400' : 'text-slate-300')} />
                                    )}
                                </div>
                            );
                        })}
                    </div>

                    {/* Progress Fill Bar if receiving */}
                    {(po.status === 'partially_received' || po.status === 'fully_received') && (
                        <div className="pt-3 border-t border-slate-100 space-y-1.5">
                            <div className="flex justify-between text-xs text-slate-500">
                                <span>Fulfilled: {totalReceived.toLocaleString()} of {totalOrdered.toLocaleString()} units</span>
                                <span className="font-bold text-teal-600">{receivedPercent}% Complete</span>
                            </div>
                            <div className="w-full bg-slate-100 rounded-full h-2 overflow-hidden border border-slate-200">
                                <motion.div
                                    initial={{ width: 0 }}
                                    animate={{ width: `${receivedPercent}%` }}
                                    transition={{ duration: 1, ease: 'easeOut' }}
                                    className="h-full bg-gradient-to-r from-teal-500 to-cyan-500 rounded-full"
                                />
                            </div>
                        </div>
                    )}
                </div>

                {/* Logistics & Financial Cards */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="bg-white border border-slate-200 rounded-2xl p-4 shadow-sm">
                        <div className="flex items-center gap-2 text-slate-500 text-xs uppercase font-bold mb-1">
                            <Store className="w-3.5 h-3.5 text-teal-600" /> Receiving Branch
                        </div>
                        <p className="font-bold text-slate-900 text-sm">{po.branchName || 'Default Branch'}</p>
                    </div>

                    <div className="bg-white border border-slate-200 rounded-2xl p-4 shadow-sm">
                        <div className="flex items-center gap-2 text-slate-500 text-xs uppercase font-bold mb-1">
                            <Building2 className="w-3.5 h-3.5 text-purple-600" /> Supplier
                        </div>
                        <p className="font-bold text-slate-900 text-sm truncate">{po.vendorName || 'Unknown Supplier'}</p>
                    </div>

                    <div className="bg-white border border-slate-200 rounded-2xl p-4 shadow-sm">
                        <div className="flex items-center gap-2 text-slate-500 text-xs uppercase font-bold mb-1">
                            <Calendar className="w-3.5 h-3.5 text-amber-600" /> Expected Delivery
                        </div>
                        <p className="font-bold text-slate-900 text-sm">{formatDate(po.expectedDeliveryDate)}</p>
                    </div>

                    <div className="bg-white border border-slate-200 rounded-2xl p-4 shadow-sm">
                        <div className="flex items-center gap-2 text-slate-500 text-xs uppercase font-bold mb-1">
                            <User className="w-3.5 h-3.5 text-blue-600" /> Issued By
                        </div>
                        <p className="font-bold text-slate-900 text-sm">{po.createdByName || 'Procurement Agent'}</p>
                    </div>
                </div>

                {/* Line Items Table */}
                <div className="bg-white border border-slate-200 rounded-3xl p-6 lg:p-8 shadow-sm space-y-6">
                    <div className="flex items-center justify-between pb-4 border-b border-slate-100">
                        <div>
                            <h3 className="text-lg font-bold text-slate-900 flex items-center gap-2">
                                <Package className="w-5 h-5 text-teal-600" />
                                <span>Commercial Line Items ({po.items?.length || 0})</span>
                            </h3>
                            <p className="text-xs text-slate-500">Products, contracted unit rates, and fulfillment status</p>
                        </div>
                        {isReceivable && (
                            <button
                                onClick={() => navigate(`/procurement/grns/new?poId=${po.id}`)}
                                className="px-4 py-2 rounded-xl bg-teal-50 hover:bg-teal-100 text-teal-700 border border-teal-200 text-xs font-bold transition-all flex items-center gap-1.5 cursor-pointer shadow-sm"
                                type="button"
                            >
                                <Truck className="w-3.5 h-3.5" />
                                <span>Receive Inbound Shipment</span>
                            </button>
                        )}
                    </div>

                    <div className="overflow-x-auto">
                        <table className="w-full text-left text-sm">
                            <thead>
                                <tr className="border-b border-slate-100 text-[11px] font-bold text-slate-500 uppercase tracking-wider">
                                    <th className="py-3 px-4">Product Catalog</th>
                                    <th className="py-3 px-4 text-right">Ordered</th>
                                    <th className="py-3 px-4 text-right">Received</th>
                                    <th className="py-3 px-4 text-right">Contracted Rate</th>
                                    <th className="py-3 px-4 text-right">VAT</th>
                                    <th className="py-3 px-4 text-right">Line Total</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                                {po.items?.map((item) => {
                                    const ordered = parseFloat(item.quantityOrdered || '0');
                                    const received = parseFloat(item.quantityReceived || '0');
                                    const isComplete = received >= ordered && ordered > 0;

                                    return (
                                        <tr key={item.id} className="hover:bg-slate-50/70 transition-colors">
                                            <td className="py-4 px-4">
                                                <p className="font-bold text-slate-900 text-sm">{item.productName || 'Inventory Item'}</p>
                                                <p className="text-xs text-slate-500 font-mono">UOM: {item.uomName || 'Unit'}</p>
                                            </td>
                                            <td className="py-4 px-4 text-right font-semibold text-slate-900">
                                                {ordered.toLocaleString()}
                                            </td>
                                            <td className="py-4 px-4 text-right font-semibold">
                                                <span className={clsx(
                                                    'px-2.5 py-1 rounded-lg text-xs font-bold inline-block',
                                                    isComplete ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' :
                                                        received > 0 ? 'bg-cyan-50 text-cyan-700 border border-cyan-200' :
                                                            'bg-slate-100 text-slate-600'
                                                )}>
                                                    {received.toLocaleString()}
                                                </span>
                                            </td>
                                            <td className="py-4 px-4 text-right font-mono text-slate-700">
                                                {formatNaira(item.unitCost)}
                                            </td>
                                            <td className="py-4 px-4 text-right font-mono text-slate-500">
                                                {item.taxRate ? `${item.taxRate}%` : '7.5%'}
                                            </td>
                                            <td className="py-4 px-4 text-right font-bold text-slate-900 font-mono">
                                                {formatNaira(item.totalCost)}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>

                    {/* Financial Reconciliation Summary */}
                    <div className="pt-6 border-t border-slate-100 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
                        <div className="text-xs text-slate-500 space-y-2 max-w-md">
                            {po.paymentTerms && (
                                <p className="text-slate-700">
                                    <span className="font-bold text-slate-900">Payment Terms: </span>
                                    {po.paymentTerms}
                                </p>
                            )}
                            {po.deliveryInstructions && (
                                <p className="text-slate-700">
                                    <span className="font-bold text-slate-900">Delivery Note: </span>
                                    {po.deliveryInstructions}
                                </p>
                            )}
                            {po.notes && (
                                <p className="text-slate-500 italic">
                                    "{po.notes}"
                                </p>
                            )}
                        </div>

                        <div className="w-full md:w-80 bg-slate-50 border border-slate-200 rounded-2xl p-5 space-y-2.5">
                            <div className="flex items-center justify-between text-xs text-slate-500">
                                <span>Subtotal</span>
                                <span className="font-semibold text-slate-900">{formatNaira(po.subtotal)}</span>
                            </div>
                            <div className="flex items-center justify-between text-xs text-slate-500">
                                <span>VAT / Taxes</span>
                                <span className="font-semibold text-slate-900">{formatNaira(po.taxAmount)}</span>
                            </div>
                            <div className="pt-3 border-t border-slate-200 flex items-center justify-between">
                                <span className="text-sm font-bold text-slate-900">Total Commitment</span>
                                <span className="text-base font-black text-teal-600">{formatNaira(po.totalAmount)}</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* ========== CONFIRMATION MODALS (LIGHT BACKGROUND) ========== */}
            <AnimatePresence>
                {modalAction && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
                        onClick={() => setModalAction(null)}
                    >
                        <motion.div
                            initial={{ scale: 0.95, opacity: 0, y: 20 }}
                            animate={{ scale: 1, opacity: 1, y: 0 }}
                            exit={{ scale: 0.95, opacity: 0, y: 20 }}
                            onClick={(e) => e.stopPropagation()}
                            className="bg-white border border-slate-200 rounded-3xl shadow-2xl w-full max-w-md p-6 sm:p-8 text-center space-y-5"
                        >
                            <div className={clsx(
                                'w-16 h-16 rounded-2xl flex items-center justify-center mx-auto',
                                modalAction === 'approve' ? 'bg-emerald-50 text-emerald-600 border border-emerald-200' : 'bg-purple-50 text-purple-600 border border-purple-200'
                            )}>
                                {modalAction === 'approve' ? <CheckCircle2 className="w-8 h-8" /> : <Send className="w-8 h-8" />}
                            </div>

                            <div>
                                <h2 className="text-xl font-bold text-slate-900">
                                    {modalAction === 'approve' ? 'Authorize Purchase Order?' : 'Dispatch Order to Supplier?'}
                                </h2>
                                <p className="text-xs text-slate-500 mt-2">
                                    {modalAction === 'approve'
                                        ? `Authorizing PO #${po.poNumber} validates commercial terms and allows sending directly to ${po.vendorName || 'the supplier'}.`
                                        : `Confirming dispatch will transition PO #${po.poNumber} to active supply chain delivery tracking.`}
                                </p>
                            </div>

                            <div className="flex items-center gap-3 pt-2">
                                <button
                                    onClick={() => setModalAction(null)}
                                    className="flex-1 px-4 py-3 rounded-xl border border-slate-300 hover:bg-slate-100 text-slate-700 font-semibold text-sm transition-all shadow-sm"
                                    type="button"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={() => {
                                        if (modalAction === 'approve') approveMutation.mutate();
                                        else sendMutation.mutate();
                                    }}
                                    disabled={approveMutation.isPending || sendMutation.isPending}
                                    className={clsx(
                                        'flex-1 px-4 py-3 rounded-xl font-bold text-sm shadow-md transition-all flex items-center justify-center gap-2 cursor-pointer text-white',
                                        modalAction === 'approve'
                                            ? 'bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 shadow-emerald-600/20'
                                            : 'bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 shadow-purple-600/20'
                                    )}
                                    type="button"
                                >
                                    {(approveMutation.isPending || sendMutation.isPending) ? (
                                        <Spinner size="xs" variant="white" />
                                    ) : (
                                        modalAction === 'approve' ? 'Approve PO' : 'Confirm Dispatch'
                                    )}
                                </button>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}